import {inject, Injectable} from '@angular/core';
import {Actions, concatLatestFrom, createEffect, ofType} from '@ngrx/effects';
import {catchError, concatMap, filter, map, of, switchMap, tap} from 'rxjs';
import {DatevWizardExportDateActions} from './datev-wizard-export-date.actions';
import {mappedHttpErrorResponseOperator, RouterActions} from '@adnova/jf-ng-components';
import {Store} from '@ngrx/store';
import {DatevWizardGetFirmaSelectors} from '../../datev-wizard-get-firma/+store/datev-wizard-get-firma.selectors';
import {DatevWizardClientIdSelectors} from '../../datev-wizard-client-id/+store/datev-wizard-client-id.selectors';
import {DatevWizardExportDateSelectors} from './datev-wizard-export-date.selectors';
import {DatevWizardCaseSelectionSelectors} from '../../datev-wizard-case-selection/+store/datev-wizard-case-selection.selectors';
import {Router} from '@angular/router';
import {DatevIncometaxExportConfigService, FullExportConfigDataDTO} from '../../../../../openapi/export-openapi';
import {MatSnackBar} from '@angular/material/snack-bar';
import {formatDateToYYYYMMDD} from '../../../../../utils/date.utils';
import {DatevWizardRetryContextService} from '../../datev-wizard-login/services/datev-wizard-retry-context.service';


@Injectable()
export class DatevWizardExportDateEffects {

  private readonly _actions$ = inject(Actions);

  private readonly _store = inject(Store);

  private readonly _router = inject(Router);

  private readonly _exportConfigService = inject(DatevIncometaxExportConfigService);

  private readonly _snackBar = inject(MatSnackBar);

  private readonly _retryContextService = inject(DatevWizardRetryContextService);

  readonly setExportDate$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardExportDateActions.setExportDate),
      concatLatestFrom(() => [
        this._store.select(DatevWizardGetFirmaSelectors.firmenId),
        this._store.select(DatevWizardClientIdSelectors.clientId)
      ]),
      concatMap(([action, firmenId, clientId]) => {
        const actions = [];

        // Nur DATEV-Connect starten wenn wir auf der export-date Seite sind und beide IDs haben
        const currentUrl = this._router.url;
        if (currentUrl.includes('/income-tax-documents/wizard/export-date/') && firmenId && clientId) {
          actions.push(DatevWizardExportDateActions.connectToDatev());
        }

        return actions;
      }),
    ),
  );

  readonly setExportDateIndependent$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardExportDateActions.setExportDateIndependent),
      concatLatestFrom(() => [
        this._store.select(DatevWizardGetFirmaSelectors.firmenId),
        this._store.select(DatevWizardClientIdSelectors.clientId)
      ]),
      concatMap(([action, firmenId, clientId]) => {
        const actions = [];

        // Nur DATEV-Connect starten wenn wir auf der export-date Seite sind und beide IDs haben
        const currentUrl = this._router.url;
        if (currentUrl.includes('/income-tax-documents/wizard/export-date/') && firmenId && clientId) {
          actions.push(DatevWizardExportDateActions.connectToDatev());
        }

        return actions;
      }),
    ),
  );

  /**
   * Effect für das Herstellen der DATEV-Verbindung (Fall 1).
   * Erstellt eine neue Export-Konfiguration und initiiert den Login-Prozess.
   * Verwendet clientId als Verbindungsname.
   */
  readonly connectToDatev$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardExportDateActions.connectToDatev),
      concatLatestFrom(() => [
        this._store.select(DatevWizardGetFirmaSelectors.firmenId),
        this._store.select(DatevWizardClientIdSelectors.clientId),
        this._store.select(DatevWizardExportDateSelectors.exportDate),
        this._store.select(DatevWizardExportDateSelectors.exportDateIndependent),
      ]),
      // Prüfe ob alle benötigten Daten vorhanden sind
      filter(([, firmenId, clientId]) => {
        if (!firmenId || !clientId) {
          this._snackBar.open('Fehler: Firma oder Client-ID fehlt', undefined, {
            duration: 5000,
            panelClass: 'error',
          });
          return false;
        }
        return true;
      }),
      switchMap(([, firmenId, clientId, exportDate, exportDateIndependent]) => {
        // Erstelle das Payload-Objekt für die neue Konfiguration (Fall 1 - Vollständiger Export)
        const payload: FullExportConfigDataDTO = {
          type: 'FullExportConfigData',
          exportSinceDocumentDate: exportDateIndependent ? undefined : formatDateToYYYYMMDD(exportDate),
        };

        // API-Call zum Erstellen der Export-Konfiguration mit clientId als Name
        return this._exportConfigService.createExportConfigRequest({
          datevClientId: clientId!,
          firmaId: firmenId!,
          name: clientId!,
          payload: payload,
        }).pipe(
          map(response => DatevWizardExportDateActions.connectToDatevSuccess({
            loginUrl: response.loginUrl
          })),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            switchMap(error => [
              DatevWizardExportDateActions.connectToDatevFailure({ error })
            ])
          ))
        );
      })
    )
  );

  /**
   * Effect für erfolgreiche Verbindung - navigiert zur DATEV Login-URL.
   * Die redirect_uri ist bereits in der Login-URL enthalten und führt zu config-details.
   */
  readonly connectToDatevSuccess$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardExportDateActions.connectToDatevSuccess),
      tap(({ loginUrl }) => {
        // Speichere aktuelle URL für Retry bei Fehler
        this._retryContextService.saveRetryUrl(window.location.href);

        // Navigiere zur DATEV Login-URL
        window.location.href = loginUrl;
      })
    ),
    { dispatch: false }
  );

  /**
   * Effect für Verbindungsfehler - zeigt Fehlermeldung an.
   */
  readonly connectToDatevFailure$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardExportDateActions.connectToDatevFailure),
      tap(({ error }) => {
        if (error.status === 409) {
          this._snackBar.open(
            'Eine Konfiguration für diesen Client existiert bereits',
            undefined,
            {
              duration: 5000,
              panelClass: 'error',
            }
          );
        } else {
          this._snackBar.open(
            'Fehler beim Verbinden mit DATEV',
            undefined,
            {
              duration: 5000,
              panelClass: 'error',
            }
          );
        }
      })
    ),
    { dispatch: false }
  );

  /**
   * Effect für Navigation zurück zur Client-ID Seite.
   * Navigiert zur Client-ID Seite mit der übergebenen Firmen-ID und Fall.
   */
  readonly goToClientId$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardExportDateActions.goToClientId),
      concatLatestFrom(() => [
        this._store.select(DatevWizardCaseSelectionSelectors.selectedCase),
      ]),
      map(([{ firmenId }, selectedCase]) => {
        const fall = selectedCase; // 'fall1' oder 'fall2'
        const url = `/datev/income-tax-documents/wizard/client-id/inhaber/${firmenId}/fall/${fall}`;
        return RouterActions.navigateByUrl({ url });
      })
    )
  );

}
